import * as amplitude from '@amplitude/analytics-browser';
import { trackClickstreamEvent } from './clickstream';
import {
  buildTrackingEnvelope,
  hasAnalyticsConsent,
  onAnalyticsConsentGranted,
} from './utils/trackingEnvelope';

const safeWindow = () => (typeof window === 'undefined' ? null : window);
const isBrokerRole = role =>
  ['administrator', 'agent', 'corretor', 'broker'].includes(
    String(role || '').toLowerCase()
  );
const accountForUser = user => {
  const accounts = Array.isArray(user.accounts) ? user.accounts : [];
  return accounts.find(account => account.id === user.account_id) || null;
};

/**
 * AnalyticsHelper class to initialize and track user analytics
 * @class AnalyticsHelper
 */
export class AnalyticsHelper {
  /**
   * @constructor
   * @param {Object} [options={}] - options for analytics
   * @param {string} [options.token] - analytics token
   * @param {boolean} [options.requiresConsent] - gate analytics until consent
   * @param {string} [options.clickstreamEndpoint] - beacon endpoint
   */
  constructor({
    token: analyticsToken,
    requiresConsent = false,
    clickstreamEndpoint = '',
  } = {}) {
    this.analyticsToken = analyticsToken;
    this.requiresConsent = Boolean(requiresConsent);
    this.clickstreamEndpoint = clickstreamEndpoint;
    this.analytics = null;
    this.user = {};
    this.currentAccount = null;
    this.pendingConsentInitializer = null;
  }

  /**
   * Initialize analytics
   * @function
   * @async
   */
  async init() {
    if (!this.analyticsToken && !this.clickstreamEndpoint) {
      return;
    }

    if (this.requiresConsent && !hasAnalyticsConsent()) {
      this.pendingConsentInitializer =
        this.pendingConsentInitializer ||
        onAnalyticsConsentGranted(() => {
          this.pendingConsentInitializer = null;
          this.init();
          this.identify(this.user);
        });
      return;
    }

    if (this.analyticsToken && !this.analytics) {
      amplitude.init(this.analyticsToken, {
        defaultTracking: false,
      });
      this.analytics = amplitude;
    }
  }

  /**
   * Identify the user
   * @function
   * @param {Object} user - User object
   */
  identify(user) {
    if (!user) {
      return;
    }

    this.user = user;
    this.currentAccount = accountForUser(user);
    if (!this.analytics) {
      return;
    }

    if (!this.user.id) {
      return;
    }

    this.analytics.setUserId(`user-${this.user.id.toString()}`);

    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('email', this.user.email);
    identifyEvent.set('name', this.user.name);
    identifyEvent.set('avatar', this.user.avatar_url);
    identifyEvent.set('role', this.user.role);
    identifyEvent.set('is_corretor', isBrokerRole(this.user.role));
    identifyEvent.set('platform', 'chatwoot_dashboard');
    this.analytics.identify(identifyEvent);

    const currentAccount = this.currentAccount;
    if (currentAccount) {
      const groupId = `account-${currentAccount.id.toString()}`;

      this.analytics.setGroup('company', groupId);

      const groupIdentify = new amplitude.Identify();
      groupIdentify.set('name', currentAccount.name);
      groupIdentify.set(
        'plan',
        currentAccount.plan_name || currentAccount.plan || ''
      );
      groupIdentify.set('market', 'real_estate');
      groupIdentify.set('platform', 'chatwoot_dashboard');
      this.analytics.groupIdentify('company', groupId, groupIdentify);
    }
  }

  /**
   * Track any event
   * @function
   * @param {string} eventName - event name
   * @param {Object} [properties={}] - event properties
   */
  track(eventName, properties = {}) {
    if (!eventName) {
      return null;
    }

    if (this.requiresConsent && !hasAnalyticsConsent()) {
      return null;
    }

    const payload = buildTrackingEnvelope(eventName, {
      ...this.defaultProperties(),
      ...properties,
    });

    const amplitudeResult = this.analytics?.track?.(eventName, payload);
    if (this.clickstreamEndpoint) {
      trackClickstreamEvent({
        endpoint: this.clickstreamEndpoint,
        event: eventName,
        properties: payload,
      });
    }

    return amplitudeResult || null;
  }

  /**
   * Track the page views
   * @function
   * @param {string} pageName - Page name
   * @param {Object} [properties={}] - Page view properties
   */
  page(pageName, properties = {}) {
    return this.track('$pageview', { pageName, ...properties });
  }

  defaultProperties() {
    const account = this.currentAccount || {};
    const role = this.user?.role || '';

    return {
      userId: this.user?.id,
      role,
      isCorretor: isBrokerRole(role),
      accountId: account.id || this.user?.account_id,
      accountPlan: account.plan_name || account.plan || '',
      market: 'real_estate',
      platform: 'chatwoot_dashboard',
    };
  }
}

// This object is shared across, the init is called in app/javascript/entrypoints/dashboard.js
export default new AnalyticsHelper(safeWindow()?.analyticsConfig);
